import type { PayloadAction } from "@reduxjs/toolkit";
import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios from "axios";
import type { IEnduserToBeInserted } from "src/features/team/state/teamSlice";
import apiUrls from "../../../api";
import type { IAppLoaderAction } from "../../../common/state/loaderHandleMiddleware";
import type { RootState } from "../../../common/state/store";
import type {
  IBuild,
  IProjectGroup,
  IProjectGroupElement,
  IProjectsPermissions,
} from "../../../model/model";
import type { IMedia } from "../../../model/unityObject";
import { emptyOrganization } from "../../profile/state/profileSlice";
import { filterGroupResults } from "../../gallery/state/filterUtil";
import { resolveDocumentId } from "../../../common/util/strapiDocumentId";
import {
  createAndAssignProjectFromAiGraph,
  deleteMultipleProject,
  deleteProject,
  duplicateProject,
  updateProject,
} from "../../projects/state/projectsSlice";

export interface ISearchIndexItem {
  id: number;
  name: string;
  type: string;
  projectGroupId: number;
  authType?: "none" | "simplified" | "classic";
  order?: number;
  updatedAt?: string;
}

export interface IProjectGroupSearch {
  query: string;
  sort: string;
  selectedId?: null | string | number;
  page: number;
  limit?: number;
  currentSearchCount?: number;
  latestListRequestId: string | null;
  // URL of the fetchProjectGroupsWithProjects request currently in flight, if
  // any; used by the thunk's `condition` to skip an identical dispatch fired
  // while the first one hasn't resolved yet (login-fanout diet, Phase 4).
  inFlightListUrl: string | null;
}

export interface INewProjectGroup {
  accessType: string;
  newName: string;
  thumbnailId: number;
  menuMediaId: number;
  themeId: number;
}

interface IProjectNewParticipantsCsv {
  userId: number;
  projectId?: number;
  withMail: number;
  file: File;
}

export interface IProjectGroupAndProjects {
  projectGroup: IProjectGroup[];
  projectsGroupWithProjects: Array<IProjectElementsOfProjectGroup>;
}

export interface IProjectGroupElementsFromCustomAPI {
  id: number;
  order: number;
  project: {
    id: number;
    name: string;
    source_thumbnail: IProjectGroupElementsFromCustomAPISourceThumbnail;
    is_deleted?: boolean;
  };
  project_group: {
    id: number;
    name: string;
    source_thumbnail: IProjectGroupElementsFromCustomAPISourceThumbnail;
  };
}

export type IProjectGroupElementNestedProject = IProjectGroupElementsFromCustomAPI["project"];
export type IProjectGroupElementNestedGroup = IProjectGroupElementsFromCustomAPI["project_group"];

export interface IProjectGroupWithProjects {
  id: number;
  organization: number;
  name: string;
  description: string;
  uuid: string;
  auth_type: string;
  source_thumbnail: {
    id: number;
    s3_url: string;
    size_in_bytes: number;
    filename: string;
  };
  createdAt: string;
  updatedAt: string;
  is_deleted: boolean;
  updated_at_ts: number;
  theme_info: number | null;
  project_group_elements: IProjectGroupElementsFromCustomAPI[];
  has_existing_build: boolean;
  creator: string;
  created_by?: string;
  publishing_history?: {
    version: number;
    projectIds: number[];
  }[];
  source_app_main_menu_media: {
    id: number;
    s3_url: string;
    size_in_bytes: number;
    filename: string;
    name?: string;
  };
}

export type ProjectGroupUpdateResponse = Omit<
  IProjectGroupWithProjects,
  | "organization"
  | "theme_info"
  | "project_group_elements"
  | "has_existing_build"
  | "creator"
  | "publishing_history"
> & {
  organization: { id: number } | number;
  theme_info: { id: number } | number | null;
};

export interface ProjectGroupUpdatePayload {
  name?: string;
  theme_info?: number | null;
  source_thumbnail?: IMedia;
  source_app_main_menu_media?: IMedia;
  project_group_elements?: { templateId: number; projectGroupId: number }[];
}

export interface IProjectGroupElementsFromCustomAPISourceThumbnail {
  id: number;
  s3_url: string;
  size_in_bytes: number;
  filename: string;
}

export interface IProjectElementsOfProjectGroup {
  projectGroupElements: Array<IProjectGroupElement>;
  projectGroupId: number;
  projectGroupName: string;
}

interface IPostProjectGroup {
  name: string;
  auth_type: string;
  source_thumbnail: number;
  organization: number;
  theme_info: number;
  created_by?: string;
  source_app_main_menu_media: number;
}

interface ISearchProjectGroup {
  name: string;
  actualGroupList: IProjectGroupWithProjects[];
}
interface ISearchProject {
  name: string;
  actualProjectList: Array<IProjectGroupElement>;
}

export interface IProjectOrder {
  order: number;
  projectId: number;
}

export interface IProjectGroupsState {
  search: IProjectGroupSearch;
  searchIndex: ISearchIndexItem[];
  searchIndexStatus: "idle" | "loading" | "loaded";
  searchIndexFetchedAt: number | null;
  allWithNestedElements: Array<IProjectGroupWithProjects>;
  current: IProjectGroupWithProjects;
  projectGroupElements: Array<IProjectElementsOfProjectGroup>;
  currentElementsForSearch: Array<IProjectGroupElement>;
  currentElements: Array<IProjectGroupElement>;
  newProjectGroup: INewProjectGroup;
  currentProjectGroupBuilds: IBuild[];
  currentProjectGroupPermissions: Array<IProjectsPermissions>;
  edited: {
    name: string;
    source_thumbnail: IMedia;
    theme_info: number | null;
    source_app_main_menu_media: IMedia;
  };
  newUserFromCsv: IEnduserToBeInserted[];
  currentProjectGroupWeight: number;
  currentModeListProjectGroup: boolean;
}

interface IFilterProjectGroup {
  type: string;
  actualGroupList: IProjectGroupWithProjects[];
}

function resetKey<K extends keyof IProjectGroupsState>(
  state: IProjectGroupsState,
  initial: IProjectGroupsState,
  key: K,
) {
  state[key] = initial[key];
}

function readThemeInfoId(value: unknown): number | null {
  if (value == null) {
    return null;
  }
  if (typeof value === "object" && value !== null && "id" in value) {
    return Number(value.id);
  }
  return Number(value);
}

export const projectGroupsInitialState: IProjectGroupsState = {
  searchIndex: [],
  searchIndexStatus: "idle",
  searchIndexFetchedAt: null,
  search: {
    query: "",
    sort: "updated_at_ts_desc",
    selectedId: null,
    page: 0,
    limit: 5,
    currentSearchCount: 0,
    latestListRequestId: null,
    inFlightListUrl: null,
  },
  allWithNestedElements: [],
  projectGroupElements: [],
  currentElementsForSearch: [],
  currentProjectGroupPermissions: [],
  current: {
    id: 0,
    name: "",
    uuid: "",
    auth_type: "simplified",
    source_thumbnail: {
      id: 0,
      s3_url: "",
      filename: "",
      size_in_bytes: 0,
    },
    organization: 0,
    updated_at_ts: 0,
    description: "",
    createdAt: "",
    updatedAt: "",
    is_deleted: false,
    theme_info: null,
    project_group_elements: [],
    has_existing_build: false,
    creator: "",
    source_app_main_menu_media: {
      id: 0,
      s3_url: "",
      filename: "",
      size_in_bytes: 0,
    },
  },
  currentElements: [],
  newProjectGroup: {
    accessType: "classic",
    newName: "",
    thumbnailId: 47,
    themeId: 1,
    menuMediaId: 5346,
  },
  currentProjectGroupBuilds: [],
  edited: {
    name: "",
    source_thumbnail: {
      createdAt: "",
      id: 0,
      name: "",
      label: "",
      organization: emptyOrganization(),
      s3_url: "",
      type: "image",
      updatedAt: "",
      filename: "",
      size_in_bytes: 0,
      media_360_tag: "",
      MD5: "",
      upload_status: "completed",
    },
    theme_info: 0,
    source_app_main_menu_media: {
      createdAt: "",
      id: 0,
      name: "",
      label: "",
      organization: emptyOrganization(),
      s3_url: "",
      type: "image",
      updatedAt: "",
      filename: "",
      size_in_bytes: 0,
      media_360_tag: "",
      MD5: "",
      upload_status: "completed",
    },
  },
  newUserFromCsv: [],
  currentProjectGroupWeight: 0,
  currentModeListProjectGroup: false,
};

export const fetchAllCurrentElements = createAsyncThunk(
  "projectGroups/getCurrentElements",

  async ({ projectGroupId }: { projectGroupId: number } & IAppLoaderAction) => {
    const response = await axios.get<Array<IProjectGroupElement>>(
      `${apiUrls.projects.projectGroupElements}?filters[project_group][id][$eq]=${projectGroupId}&populate[project][populate][source_thumbnail]=true&populate[project][populate][enabled_languages]=true&populate[project][populate][project_tags]=true&populate[project_group][populate][source_app_main_menu_media]=true`,
    );

    return { data: response.data };
  },
);

export const fetchSearchIndex = createAsyncThunk(
  "projectGroups/fetchSearchIndex",
  // eslint-disable-next-line no-empty-pattern
  async ({}: { force?: boolean } & IAppLoaderAction) => {
    const response = await axios.get<Array<ISearchIndexItem>>(`${apiUrls.projects.searchIndex}`);

    return { data: response.data };
  },
);

type FetchProjectGroupsWithProjectsArg = {
  limit?: number;
  start?: number;
  sort?: string;
  projectGroupId?: number;
  filterByPublishedProject?: boolean;
  query?: string;
} & IAppLoaderAction;

const buildProjectGroupsWithProjectsUrl = ({
  limit = 5,
  start,
  sort = "updated_at_ts_desc",
  projectGroupId,
  filterByPublishedProject = false,
  query,
}: FetchProjectGroupsWithProjectsArg): string => {
  const params = new URLSearchParams();
  if (limit) {
    params.append("limit", limit.toString());
  }
  if (start) {
    params.append("start", start.toString());
  }
  if (sort) {
    params.append("sort", sort);
  }
  if (projectGroupId) {
    params.append("projectGroupId", projectGroupId.toString());
  }
  if (query) {
    params.append("query", query);
  }

  params.append("filterByPublishedProject", filterByPublishedProject.toString());

  return `${apiUrls.projects.projectGroupsWithProjects}${
    params.toString() ? "?withTotalCount=true&" + params.toString() : ""
  }`;
};

export const fetchProjectGroupsWithProjects = createAsyncThunk(
  "projectGroups/getProjectGroupsWithProjects",
  async (arg: FetchProjectGroupsWithProjectsArg) => {
    const url = buildProjectGroupsWithProjectsUrl(arg);

    const response = await axios.get<{ results: IProjectGroupWithProjects[]; totalCount: number }>(
      url,
    );

    return { data: response.data };
  },
  {
    // Skip a second identical dispatch fired while the first is still in
    // flight (login-fanout diet, Phase 4): App.tsx's boot effect and
    // Navigation/CreatorDashboard's mount effect can both fire this on
    // login. Callers chaining `.then()` still resolve, they just share the
    // in-flight promise's result.
    condition: (arg, { getState }) =>
      (getState() as RootState).projectGroups.search.inFlightListUrl !==
      buildProjectGroupsWithProjectsUrl(arg),
  },
);

export const fetchSpecificProjectGroupWithProjects = createAsyncThunk(
  "projectGroups/fetchSpecificProjectGroupWithProjects",
  async ({ projectGroupId }: { projectGroupId?: number } & IAppLoaderAction) => {
    const url = `${apiUrls.projects.projectGroupsWithProjects}?projectGroupId=${projectGroupId}`;
    const response = await axios.get<IProjectGroupWithProjects[]>(url);
    const data = Array.isArray(response.data) ? response.data[0] : response.data;
    return { data };
  },
);

export const uploadNewParticipant = createAsyncThunk(
  "projectGroups/uploadNewParticipant",
  async ({ userId, projectId, withMail, file }: IProjectNewParticipantsCsv & IAppLoaderAction) => {
    const data = {
      files: {
        csvFile: file,
      },
      data: JSON.stringify({
        userId: userId.toString(),
        withMail: withMail.toString(),
        projectId: projectId?.toString(),
      }),
    };

    const response = await axios.post<Array<IEnduserToBeInserted>>(`${apiUrls.csv.add}`, data, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });

    return { data: response.data };
  },
);

export const postProjectGroup = createAsyncThunk(
  "projectGroups/post",
  async ({
    name,
    source_thumbnail,
    auth_type,
    organization,
    theme_info,
    created_by,
    source_app_main_menu_media,
  }: IPostProjectGroup & IAppLoaderAction) => {
    const response = await axios.post<IProjectGroup>(
      `${apiUrls.projects.projectGroups}?populate[0]=organization&populate[1]=source_thumbnail&populate[2]=source_app_main_menu_media`,
      {
        name,
        auth_type,
        source_thumbnail,
        organization,
        theme_info,
        created_by,
        source_app_main_menu_media,
      },
    );
    return { data: response.data };
  },
);

export const updateProjectGroup = createAsyncThunk(
  "projectGroups/updateProjectGroup",
  async (
    {
      id,
      payload,
    }: {
      id: number;
      payload: ProjectGroupUpdatePayload;
    } & IAppLoaderAction,
    { rejectWithValue },
  ) => {
    try {
      const documentId = await resolveDocumentId(apiUrls.projects.projectGroups, id);
      const response = await axios.put<ProjectGroupUpdateResponse>(
        `${apiUrls.projects.projectGroups}/${documentId}`,
        {
          ...payload,
        },
      );
      return response.data;
    } catch (err) {
      if (axios.isAxiosError(err)) {
        return rejectWithValue(err?.response?.data);
      } else {
        throw err;
      }
    }
  },
);

export const deleteProjectGroup = createAsyncThunk(
  "projectGroups/delete",
  async ({ id }: { id: number } & IAppLoaderAction) => {
    const documentId = await resolveDocumentId(apiUrls.projects.projectGroups, id);
    const response = await axios.put<IProjectGroup>(
      `${apiUrls.projects.projectGroups}/${documentId}`,
      {
        is_deleted: true,
      },
    );
    return { data: response.data };
  },
);

export const deleteMultipleProjectGroup = createAsyncThunk(
  "projectGroups/deleteMultipleProjectGroup",
  async ({ projectGroupListId }: { projectGroupListId: Array<number> } & IAppLoaderAction) => {
    const response = await axios.delete<Array<number>>(
      `${apiUrls.projects.multipleProjectGroups}?ids=${projectGroupListId.toString()}`,
    );
    return { data: response.data };
  },
);

export const deleteTheme = createAsyncThunk(
  "projectGroups/deleteTheme",
  async ({ themeId }: { themeId: number } & IAppLoaderAction) => {
    const documentId = await resolveDocumentId(apiUrls.themeInfos, themeId);
    const response = await axios.delete<unknown>(`${apiUrls.themeInfos}/${documentId}`);

    return { data: response.data };
  },
);

export const getProjectGroupPermissionsFromServer = createAsyncThunk(
  "projects/getProjectGroupPermissionsFromServer",

  async (
    { projectGroupId }: { projectGroupId: number } & IAppLoaderAction,
    { rejectWithValue },
  ) => {
    try {
      const response = await axios.get<Array<IProjectsPermissions>>(
        `${apiUrls.projects.projectUserPermissions}?filters[project_group][id][$eq]=${projectGroupId}&populate[project]=true&populate[project_group]=true&populate[user][populate][role]=true&populate[user][populate][source_avatar]=true`,
      );
      return { data: response.data };
    } catch (err) {
      console.error(err);
      if (axios.isAxiosError(err)) {
        return rejectWithValue(err?.response?.data);
      } else {
        throw err;
      }
    }
  },
);

export const reorderProjects = createAsyncThunk(
  "projects/reorderProjects",
  async ({ orders }: { orders: Array<IProjectOrder> } & IAppLoaderAction) => {
    const response = await axios.post<Array<IProjectGroupElement>>(
      `${apiUrls.projects.reorderProjects}`,
      {
        orders,
      },
    );
    return { data: response.data };
  },
);

export const getProjectGroupSize = createAsyncThunk(
  "projects/getProjectGroupSize",
  async ({ projectsIds }: { projectsIds: string } & IAppLoaderAction) => {
    const response = await axios.get(`${apiUrls.projects.projectGroupSize}?ids=${projectsIds}`);
    return { data: Number(response.data) };
  },
);

export const duplicateProjectGroup = createAsyncThunk(
  "projectGroups/duplicateProjectGroups",
  async (
    {
      thumbnail,
      projectGroup,
      newName,
    }: {
      thumbnail: number;
      projectGroup: number;
      newName: string;
    } & IAppLoaderAction,
    { rejectWithValue },
  ) => {
    try {
      const params = new URLSearchParams({
        projectGroupId: String(projectGroup),
        thumbnailId: String(thumbnail),
        newProjectGroupName: newName,
      });
      const response = await axios.post<{
        duplicatedProjectGroup: IProjectGroupWithProjects;
      }>(`${apiUrls.projects.duplicateProjectGroup}?${params.toString()}`);
      const duplicatedProjectGroupId = response.data?.duplicatedProjectGroup?.id;
      if (!duplicatedProjectGroupId) {
        return { data: response.data };
      }
      const duplicatedProjectGroupWithProjects = await axios
        .get<IProjectGroupWithProjects[]>(
          `${apiUrls.projects.projectGroupsWithProjects}?projectGroupId=${duplicatedProjectGroupId}`,
        )
        .then((res) => (Array.isArray(res.data) ? res.data[0] : res.data))
        .catch(() => null);
      return {
        data: {
          ...response.data,
          duplicatedProjectGroup:
            duplicatedProjectGroupWithProjects ?? response.data.duplicatedProjectGroup,
        },
      };
    } catch (err) {
      if (axios.isAxiosError(err)) {
        return rejectWithValue(err?.response?.data);
      } else {
        throw err;
      }
    }
  },
);

function normalizeProjectGroupUpdate(payload: ProjectGroupUpdateResponse) {
  return {
    ...payload,
    organization: Number(
      typeof payload.organization === "object" ? payload.organization.id : payload.organization,
    ),
    theme_info: readThemeInfoId(payload.theme_info),
  };
}

export const projectGroupSlice = createSlice({
  name: "projectGroups",
  initialState: projectGroupsInitialState,
  extraReducers: (builder) => {
    builder.addCase(fetchAllCurrentElements.fulfilled, (state, { payload }) => {
      const currentElements = payload.data;
      state.currentElementsForSearch = currentElements;
      state.currentElements = currentElements;
    });

    builder.addCase(fetchProjectGroupsWithProjects.pending, (state, action) => {
      state.search.latestListRequestId = action.meta.requestId;
      state.search.inFlightListUrl = buildProjectGroupsWithProjectsUrl(action.meta.arg);
    });

    builder.addCase(fetchProjectGroupsWithProjects.fulfilled, (state, action) => {
      if (action.meta.requestId === state.search.latestListRequestId) {
        state.search.inFlightListUrl = null;
      }
      if (action.meta.requestId !== state.search.latestListRequestId) {
        return;
      }
      const { payload } = action;
      const newResults = payload.data.results;
      const selectedId = state.search.selectedId;
      if (selectedId && !newResults.some((pg) => pg.id === Number(selectedId))) {
        const kept = state.allWithNestedElements.find((pg) => pg.id === Number(selectedId));
        if (kept) {
          newResults.unshift(kept);
        }
      }
      state.allWithNestedElements = newResults;
      state.search.currentSearchCount = payload.data.totalCount;
    });

    builder.addCase(fetchProjectGroupsWithProjects.rejected, (state, action) => {
      if (action.meta.requestId === state.search.latestListRequestId) {
        state.search.inFlightListUrl = null;
      }
    });

    builder.addCase(postProjectGroup.fulfilled, (state, { payload }) => {
      const reworkedPayload = {
        id: Number(payload.data.id),
        organization: Number(payload.data.organization.id),
        name: payload.data.name,
        description: payload.data.description,
        uuid: payload.data.uuid,
        auth_type: payload.data.auth_type,
        source_thumbnail: {
          id: Number(payload.data.source_thumbnail.id),
          s3_url: payload.data.source_thumbnail.s3_url,
          size_in_bytes: payload.data.source_thumbnail.size_in_bytes,
          filename: payload.data.source_thumbnail.filename,
        },
        createdAt: payload.data.created_at,
        updatedAt: payload.data.updated_at,
        is_deleted: payload.data.is_deleted,
        updated_at_ts: Number(payload.data.updated_at_ts),
        theme_info: readThemeInfoId(payload.data.theme_info),
        project_group_elements: [],
      };

      const updatedAllWithNestedElements = [
        reworkedPayload,
        ...state.allWithNestedElements,
      ] as IProjectGroupWithProjects[];

      state.allWithNestedElements = updatedAllWithNestedElements;
      state.searchIndex = [];
      state.searchIndexStatus = "idle";
      state.searchIndexFetchedAt = null;
    });
    builder.addCase(deleteProjectGroup.fulfilled, (state, { payload }) => {
      const updatedProjectElementsOfProjectGroup = [...state.projectGroupElements].filter(
        (pge: IProjectElementsOfProjectGroup) =>
          Number(pge.projectGroupId) !== Number(payload.data.id),
      ) as Array<IProjectElementsOfProjectGroup>;

      const updatedAllWithNestedElements = [...state.allWithNestedElements].filter(
        (pge: IProjectGroupWithProjects) => Number(pge.id) !== Number(payload.data.id),
      );

      state.projectGroupElements = updatedProjectElementsOfProjectGroup;
      state.allWithNestedElements = updatedAllWithNestedElements;
      state.searchIndex = [];
      state.searchIndexStatus = "idle";
      state.searchIndexFetchedAt = null;
      state.search.currentSearchCount = Math.max(0, (state.search.currentSearchCount ?? 0) - 1);
    });
    builder.addCase(deleteMultipleProjectGroup.fulfilled, (state, { payload }) => {
      const updatedProjectElementsOfProjectGroup = [...state.projectGroupElements].filter(
        (pge: IProjectElementsOfProjectGroup) => !payload.data.includes(Number(pge.projectGroupId)),
      ) as Array<IProjectElementsOfProjectGroup>;

      const updatedAllWithNestedElements = [...state.allWithNestedElements].filter(
        (pge: IProjectGroupWithProjects) => !payload.data.includes(Number(pge.id)),
      );

      state.projectGroupElements = updatedProjectElementsOfProjectGroup;
      state.allWithNestedElements = updatedAllWithNestedElements;
      state.searchIndex = [];
      state.searchIndexStatus = "idle";
      state.searchIndexFetchedAt = null;
      state.search.currentSearchCount = Math.max(
        0,
        (state.search.currentSearchCount ?? 0) - payload.data.length,
      );
    });

    builder.addCase(getProjectGroupSize.fulfilled, (state, { payload }) => {
      state.currentProjectGroupWeight = payload.data;
    });
    builder.addCase(updateProjectGroup.fulfilled, (state, { payload }) => {
      const patch = normalizeProjectGroupUpdate(payload);
      state.allWithNestedElements = state.allWithNestedElements.map((pg) =>
        Number(pg.id) === Number(patch.id) ? { ...pg, ...patch } : pg,
      );
      if (Number(state.current.id) === Number(patch.id)) {
        state.current = { ...state.current, ...patch };
      }
      state.edited = { ...state.edited, name: patch.name, theme_info: patch.theme_info };
      state.searchIndex = [];
      state.searchIndexStatus = "idle";
      state.searchIndexFetchedAt = null;
    });
    builder.addCase(fetchSpecificProjectGroupWithProjects.fulfilled, (state, { payload }) => {
      const specificProjectGroup = payload.data;
      if (!specificProjectGroup) return;

      const exists = state.allWithNestedElements.some((pg) => pg.id === specificProjectGroup.id);
      if (exists) {
        state.allWithNestedElements = state.allWithNestedElements.map((pg) =>
          pg.id === specificProjectGroup.id ? { ...specificProjectGroup } : pg,
        );
      } else {
        state.allWithNestedElements = [specificProjectGroup, ...state.allWithNestedElements];
      }

      state.current = specificProjectGroup;
    });
    builder.addCase(fetchSearchIndex.pending, (state) => {
      state.searchIndexStatus = "loading";
    });
    builder.addCase(fetchSearchIndex.fulfilled, (state, { payload }) => {
      state.searchIndex = payload.data;
      state.searchIndexStatus = "loaded";
      state.searchIndexFetchedAt = Date.now();
    });
    builder.addCase(fetchSearchIndex.rejected, (state) => {
      state.searchIndexStatus = "idle";
    });

    builder.addCase(duplicateProjectGroup.fulfilled, (state, { payload }) => {
      const duplicatedProjectGroup = payload.data.duplicatedProjectGroup;
      if (duplicatedProjectGroup) {
        const updatedAllWithNestedElements = [
          duplicatedProjectGroup,
          ...state.allWithNestedElements.filter((pg) => pg.id !== duplicatedProjectGroup.id),
        ] as IProjectGroupWithProjects[];
        state.allWithNestedElements = updatedAllWithNestedElements;
      }
      state.searchIndex = [];
      state.searchIndexStatus = "idle";
      state.searchIndexFetchedAt = null;
    });

    builder.addCase(duplicateProject.fulfilled, (state) => {
      state.searchIndex = [];
      state.searchIndexStatus = "idle";
      state.searchIndexFetchedAt = null;
    });
    builder.addCase(updateProject.fulfilled, (state) => {
      state.searchIndex = [];
      state.searchIndexStatus = "idle";
      state.searchIndexFetchedAt = null;
    });
    builder.addCase(deleteProject.fulfilled, (state) => {
      state.searchIndex = [];
      state.searchIndexStatus = "idle";
      state.searchIndexFetchedAt = null;
    });
    builder.addCase(deleteMultipleProject.fulfilled, (state) => {
      state.searchIndex = [];
      state.searchIndexStatus = "idle";
      state.searchIndexFetchedAt = null;
    });
    builder.addCase(createAndAssignProjectFromAiGraph.fulfilled, (state) => {
      state.searchIndex = [];
      state.searchIndexStatus = "idle";
      state.searchIndexFetchedAt = null;
    });
  },

  reducers: {
    setSelectedProjectGroup: (
      _state: IProjectGroupsState,
      action: PayloadAction<IProjectGroupWithProjects>,
    ) => {
      _state.current = action.payload;
      _state.edited.name = action.payload.name;
      _state.edited.source_thumbnail = { ...action.payload.source_thumbnail } as IMedia;
      _state.edited.source_app_main_menu_media = {
        ...action.payload.source_app_main_menu_media,
      } as IMedia;
    },
    setSelectedProjectGroupFromId: (_state: IProjectGroupsState, action: PayloadAction<number>) => {
      const moduleId = action.payload;
      const pgObject = _state.allWithNestedElements.find((project) => project.id === moduleId);

      if (pgObject) {
        _state.current = pgObject;
        _state.edited.name = pgObject.name;
        _state.edited.source_thumbnail = { ...pgObject.source_thumbnail } as IMedia;
        _state.edited.source_app_main_menu_media = {
          ...pgObject.source_app_main_menu_media,
        } as IMedia;
      }
    },
    setSelectedProjectGroupFromProjectGroupId: (
      _state: IProjectGroupsState,
      action: PayloadAction<number>,
    ) => {
      const projectGroup = _state.allWithNestedElements.find(
        (pg) => Number(pg.id) === Number(action.payload),
      );
      if (projectGroup) {
        _state.current = projectGroup;
        _state.edited.name = projectGroup.name;
      }
    },
    setCurrentProjectGroupName: (_state: IProjectGroupsState, action: PayloadAction<string>) => {
      _state.current.name = action.payload;
    },
    setNewProjectGroup: (_state: IProjectGroupsState, action: PayloadAction<INewProjectGroup>) => {
      _state.newProjectGroup = { ...action.payload };
    },
    setUpdateProject: (
      state: IProjectGroupsState,
      action: PayloadAction<Array<IProjectGroupElement>>,
    ) => {
      state.currentElements = action.payload;
    },
    setUserFromCsv: (state: IProjectGroupsState, action: PayloadAction<IEnduserToBeInserted[]>) => {
      state.newUserFromCsv = action.payload;
    },
    addCurrentElement: (
      state: IProjectGroupsState,
      action: PayloadAction<IProjectGroupElement>,
    ) => {
      const updatedCurrentElements = [...state.currentElements, action.payload];
      const projectGroupId = action.payload.project_group.id;

      state.currentElements = updatedCurrentElements;
      state.currentElementsForSearch = updatedCurrentElements;

      const updateProjectGroupElements = (array: IProjectGroupWithProjects[]) => {
        return array.map((projectGroup) => {
          if (projectGroup.id === projectGroupId) {
            const updatedProjectGroupElements = [
              ...projectGroup.project_group_elements,
              action.payload,
            ] as typeof projectGroup.project_group_elements;
            return {
              ...projectGroup,
              project_group_elements: updatedProjectGroupElements,
            };
          }
          return projectGroup;
        });
      };

      state.allWithNestedElements = updateProjectGroupElements(state.allWithNestedElements);
    },
    setCurrentElementsFromCurrentProjectGroup: (state: IProjectGroupsState) => {
      state.currentElements = state.current.project_group_elements as any;
      state.currentElementsForSearch = state.current.project_group_elements as any;
    },
    removeElementsOfProject: (
      state: IProjectGroupsState,
      action: PayloadAction<string | number>,
    ) => {
      const updatedCurrentElements = [...state.currentElements].filter(
        (pge: IProjectGroupElement) => {
          return pge.project && Number(pge.project.id) !== Number(action.payload);
        },
      );
      state.currentElements = updatedCurrentElements;
      state.currentElementsForSearch = updatedCurrentElements;

      const updatedPgList = state.allWithNestedElements.map((pgwne) => {
        return {
          ...pgwne,
          project_group_elements: [...pgwne.project_group_elements]
            .sort((a, b) => a.order - b.order)
            .filter((pge) => pge.project && Number(pge.project.id) !== Number(action.payload)),
        };
      });

      const updatedCurrentProjectGroup = state.current.project_group_elements
        ? {
            ...state.current,
            project_group_elements: [...state.current.project_group_elements]
              .sort((a, b) => a.order - b.order)
              .filter((pge) => pge.project && Number(pge.project.id) !== Number(action.payload)),
          }
        : state.current;

      state.allWithNestedElements = updatedPgList;
      state.current = updatedCurrentProjectGroup;
    },
    setCurrentProjectGroupPermissions: (
      state: IProjectGroupsState,
      action: PayloadAction<IProjectsPermissions[]>,
    ) => {
      state.currentProjectGroupPermissions = action.payload;
    },
    setFilterProjectGroup: (
      state: IProjectGroupsState,
      action: PayloadAction<IFilterProjectGroup>,
    ) => {
      const filter = action.payload.type;
      const list = action.payload.actualGroupList;
      const splitFilter = filter?.split("_");
      const getFilteredList = filterGroupResults(list, splitFilter);
      state.allWithNestedElements = getFilteredList;
    },
    setSearchProjectGroups: (
      state: IProjectGroupsState,
      action: PayloadAction<ISearchProjectGroup>,
    ) => {
      const nameProjectGroup = action.payload.name;
      if (nameProjectGroup !== "") {
        const filterNameForSearch = action.payload.actualGroupList.filter((group) =>
          group.name && group.name !== "" ? action.payload.name.includes(group.name) : false,
        );
        state.allWithNestedElements = filterNameForSearch;
      } else {
        state.allWithNestedElements = action.payload.actualGroupList;
      }
    },
    setSearchProject: (state: IProjectGroupsState, action: PayloadAction<ISearchProject>) => {
      const nameProject = action.payload.name;
      if (nameProject !== "") {
        const filterNameForSearch = action.payload.actualProjectList.filter((group) =>
          group.project?.name && group.project.name !== ""
            ? action.payload.name.includes(group.project.name)
            : false,
        );
        state.currentElements = filterNameForSearch;
      } else {
        state.currentElements = action.payload.actualProjectList;
      }
    },
    setCurrentElements: (
      state: IProjectGroupsState,
      action: PayloadAction<Array<IProjectGroupElement>>,
    ) => {
      state.currentElements = action.payload;
      state.currentElementsForSearch = action.payload;
    },
    setCurrentModeListProjectGroup: (
      state: IProjectGroupsState,
      action: PayloadAction<boolean>,
    ) => {
      state.currentModeListProjectGroup = action.payload;
    },
    setEdited: (state: IProjectGroupsState) => {
      state.edited = {
        name: state.current.name,
        source_thumbnail: state.current
          .source_thumbnail as IProjectGroupElementsFromCustomAPISourceThumbnail & IMedia,
        theme_info: state.current.theme_info,
        source_app_main_menu_media: state.current.source_app_main_menu_media as IMedia,
      };
    },
    setEditedName: (state: IProjectGroupsState, action: PayloadAction<string>) => {
      state.edited.name = action.payload;
    },
    setEditedThumbnail: (state: IProjectGroupsState, action: PayloadAction<IMedia>) => {
      state.edited.source_thumbnail = action.payload;
    },
    clearProjectGroups: (state: IProjectGroupsState) => {
      (Object.keys(state) as Array<keyof typeof state>).forEach((key) => {
        resetKey(state, projectGroupsInitialState, key);
      });
    },
    setProjectGroupsWithProjects: (
      state: IProjectGroupsState,
      action: PayloadAction<IProjectGroupWithProjects[]>,
    ) => {
      state.allWithNestedElements = action.payload;
    },
    updateProjectGroupThumbnail: (state: IProjectGroupsState, action: PayloadAction<IMedia>) => {
      const bingo = (pg: IProjectGroupWithProjects) =>
        Number(pg.source_thumbnail.id) === Number(action.payload.id);

      state.allWithNestedElements = [...state.allWithNestedElements].map(
        (pg: IProjectGroupWithProjects) => {
          if (bingo(pg)) {
            return {
              ...pg,
              source_thumbnail: action.payload,
            } as IProjectGroupWithProjects;
          } else {
            return pg;
          }
        },
      );
      state.current.source_thumbnail =
        action.payload as IProjectGroupElementsFromCustomAPISourceThumbnail & IMedia;
      state.edited.source_thumbnail = action.payload;
    },
    setEditedMenuMedia: (state: IProjectGroupsState, action: PayloadAction<IMedia>) => {
      state.edited.source_app_main_menu_media = action.payload;
    },
    setCurrentProjectGroupAsEdited: (state: IProjectGroupsState) => {
      state.edited = {
        name: state.current.name,
        source_thumbnail: state.current.source_thumbnail as IMedia,
        theme_info: state.current.theme_info ?? 0,
        source_app_main_menu_media: state.current.source_app_main_menu_media as IMedia,
      };
    },
    setSearchQuery(state, action: PayloadAction<string>) {
      state.search.query = action.payload;
    },
    setSearchSort(state, action: PayloadAction<string>) {
      if (state.search.sort !== action.payload) {
        state.search.query = "";
        state.search.selectedId = null;
      }
      state.search.sort = action.payload;
    },
    setSearchSelectedId(state, action: PayloadAction<string | number | null>) {
      state.search.selectedId = action.payload;
    },
    setSearchPage(state, action: PayloadAction<number>) {
      state.search.page = action.payload;
    },
    setSearchLimit(state, action: PayloadAction<number>) {
      state.search.limit = action.payload;
    },
    clearSearch(state) {
      state.search = {
        query: "",
        sort: "updated_at_ts_desc",
        selectedId: null,
        page: 0,
        latestListRequestId: null,
        inFlightListUrl: null,
      };
    },
    clearSort(state) {
      state.search.sort = "0";
    },
  },
});

export const projectGroupReducer = projectGroupSlice.reducer;

export const getAllProjectGroupsWithNestedElements = (state: RootState) =>
  state.projectGroups.allWithNestedElements;

export const getCurrentProjectForSearch = (state: RootState) =>
  state.projectGroups.currentElementsForSearch;

export const getSelectedProjectGroup = (state: RootState) => state.projectGroups.current;

export const getEditedProjectGroup = (state: RootState) => state.projectGroups.edited;

export const getCurrentElements = (state: RootState) =>
  [...state.projectGroups.currentElements].sort((a, b) => a.order - b.order);

export const getNewProjectGroup = (state: RootState) => state.projectGroups.newProjectGroup;

export const getCurrentProjectGroupBuilds = (state: RootState) =>
  state.projectGroups.currentProjectGroupBuilds;

export const getCurrentProjectGroupPermissions = (state: RootState) =>
  state.projectGroups.currentProjectGroupPermissions;
export const getCurrentUsersFromCsv = (state: RootState) => state.projectGroups.newUserFromCsv;

export const getCurrentProjectGroupWeight = (state: RootState) =>
  state.projectGroups.currentProjectGroupWeight;

export const getCurrentModeListProjectGroup = (state: RootState) =>
  state.projectGroups.currentModeListProjectGroup;

export const getSearchIndex = (state: RootState) => state.projectGroups.searchIndex;
export const getSearchState = (state: RootState) => state.projectGroups.search;
export const getSearchLimit = (state: RootState) => state.projectGroups.search.limit;
export const getCurrentSearchCount = (state: RootState) =>
  state.projectGroups.search.currentSearchCount;

export const {
  setUserFromCsv,
  setSelectedProjectGroup,
  setSelectedProjectGroupFromId,
  setCurrentProjectGroupName,
  setSelectedProjectGroupFromProjectGroupId,
  setNewProjectGroup,
  addCurrentElement,
  setCurrentModeListProjectGroup,
  removeElementsOfProject,
  setCurrentProjectGroupPermissions,
  setEdited,
  setEditedName,
  setEditedThumbnail,
  clearProjectGroups,
  setCurrentElements,
  setCurrentElementsFromCurrentProjectGroup,
  updateProjectGroupThumbnail,
  setProjectGroupsWithProjects,
  setEditedMenuMedia,
  setCurrentProjectGroupAsEdited,
  setSearchQuery,
  setSearchSort,
  setSearchSelectedId,
  clearSearch,
  setSearchPage,
  setSearchLimit,
} = projectGroupSlice.actions;
